import { createSlice } from '@reduxjs/toolkit';
import { LogState, LogMessage } from './types';

const initialState: LogState = {
  logs: [],
};

const slice = createSlice({
  name: 'log',
  initialState,
  reducers: {
    addLog: (state, { payload }) => ({
      ...state,
      logs: [...state.logs, payload],
    }),
    removeLog: (state, { payload }) => ({
      ...state,
      logs: state.logs.filter((log: LogMessage) => log !== payload),
    }),
    resetLogs: () => initialState,
    filterLogsByTimestamp: (state, { payload }) => {
      const { lastAcceptedTimestamp, sentLogs } = payload;

      const sentLogHashSet = new Set(sentLogs.map((log: LogMessage) => log.hash));

      // Keep logs that are either:
      // 1. Newer than lastAcceptedTimestamp (not yet accepted by server)
      // 2. NOT in sentLogs (added after sync started)
      const logsToKeep = state.logs.filter((log: LogMessage) => {
        if (!sentLogHashSet.has(log.hash)) {
          return true;
        }
        // If log is newer than last accepted timestamp, keep it for retry
        const logTimestamp = typeof log.date === 'number' ? log.date : 0;
        return logTimestamp > lastAcceptedTimestamp;
      });

      return {
        ...state,
        logs: logsToKeep,
      };
    },
    incrementLogCount: (state, { payload: hash }) => {
      const logIndex = state.logs.findIndex((log: LogMessage) => log.hash === hash);
      if (logIndex !== -1) {
        const updatedLogs = [...state.logs];
        updatedLogs[logIndex] = {
          ...updatedLogs[logIndex],
          count: (updatedLogs[logIndex].count || 1) + 1,
        } as LogMessage;
        return { ...state, logs: updatedLogs };
      }
      return state;
    },
    removeLogs: (state, { payload: logsToRemove }) => ({
      ...state,
      logs: state.logs.filter((log: LogMessage) => !logsToRemove.includes(log)),
    }),
  },
});

// Reducer
export default slice.reducer;

// Actions
export const {
  addLog,
  removeLog,
  resetLogs,
  filterLogsByTimestamp,
  incrementLogCount,
  removeLogs,
} = slice.actions;
